import { Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, SimpleChanges, HostAttributeToken, inject } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  AppService,
  LOGGEDINUSER,
  OfficeMemberService,
  ProfileInfo,
  TeamMemberService,
  User,
} from '@lc/core';
import { Observable, timer } from 'rxjs';
import { debounce, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AsyncPipe } from '@angular/common';
import { ChipsComponent } from '@lc/ui';
import { AvatarComponent } from '../../../avatar/avatar.component';

@Component({
  selector: 'lc-agent-search',
  templateUrl: './agent-search.component.html',
  styleUrls: ['./agent-search.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AgentSearchComponent),
      multi: true,
    },
  ],
  imports: [
    ChipsComponent,
    FormsModule,
    ReactiveFormsModule,
    AvatarComponent,
    AsyncPipe,
  ],
})
export class AgentSearchComponent implements ControlValueAccessor, OnInit, OnChanges {
  max: number;
  label = inject(new HostAttributeToken('label'), { optional: true }) ?? 'Members';
  placeholder = inject(new HostAttributeToken('placeholder'), { optional: true }) ?? 'Add member';
  emptyPlaceholder = inject(new HostAttributeToken('emptyPlaceholder'), { optional: true }) ?? 'Add new members';
  private teamMemberService = inject(TeamMemberService);
  private officeMemberService = inject(OfficeMemberService);
  private store = inject<Store<any>>(Store);

  memberSearch$: Observable<ProfileInfo[]>;
  searchControl: FormControl;
  appkey: string;
  noResultDisplayText: string;

  @Input()
    idPrefix = 'members';

  @Input()
    formControl: FormControl;

  @Input()
    selected: ProfileInfo[] = [];

  @Input()
    selectedIds: string[] = [];

  @Input()
    blacklistedIds: string[] = [];

  @Input()
    excludeTeamMembers: boolean = false;

  @Input()
    onlyDelegatedUsers: boolean = false;

  @Input()
    searchBy: string = '';

  @Input()
    filterRealEstateRole: string = '';

  @Input()
    filterLicenseNumber: boolean = false;

  @Input()
    office: boolean = false;

  @Input()
    delegate: boolean = false;

  @Output()
  readonly selectedChanged = new EventEmitter<ProfileInfo[]>();

  readonly validators: Validators;

  constructor() {
    const max = inject(new HostAttributeToken('max'), { optional: true });
    this.max = max ? +this.max : undefined;
  }

  ngOnInit() {
    this.appkey = AppService.get('applicationName');
    this.noResultDisplayText = `No user found. Please ensure the user you are searching for has logged into ${this.appkey} and completed their profile`;
    this.initializeSearch();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedIds) {
      if ((this.selectedIds || []).length > 0 && !this.delegate) {
        // We need to find these agent/names to select them again.
        const memberService = this.office ? this.officeMemberService : this.teamMemberService;
        memberService.searchAgentsById(this.selectedIds, this.searchBy).subscribe(
          (agents) => {
            this.updateAgents(agents, false);
          },
          (error) => {
            throw new Error(error);
          },
        );
      } else {
        this.updateAgents([], false);
      }
    }
  }

  public updateAgents(agents: ProfileInfo[], emit: boolean = true) {
    if (!this.selected || !Array.isArray(this.selected)) this.selected = [];
    this.selected.splice(0, this.selected.length + 1, ...agents);
    if (emit) {
      this.selectedChanged.emit(this.selected);
    }
    this.formControl.setValue(this.selected);
  }

  writeValue(value: any) {
    if (value !== undefined) {
      this.selected = value;
    }
  }
  registerOnChange(fn) {}

  registerOnTouched() {}

  private initializeSearch() {
    const memberService = this.office ? this.officeMemberService : this.teamMemberService;
    this.searchControl = new FormControl();

    if (this.delegate) {
      this.memberSearch$ = this.searchControl.valueChanges.pipe(
        withLatestFrom(this.store.select(LOGGEDINUSER).pipe(map((user: User) => user))),
        debounce(() => timer(500)), // NOTE: The debounce time on the chips input is already 250 ms
        switchMap(([searchTerm, user]) => this.officeMemberService
          .searchAllAgents(searchTerm)
          .pipe(map((members) => members.filter((member) => member.userId !== user._id)))),
      );
    } else {
      this.memberSearch$ = this.searchControl.valueChanges.pipe(
        withLatestFrom(this.store.select(LOGGEDINUSER).pipe(map((user: User) => user))),
        debounce(() => timer(500)), // NOTE: The debounce time on the chips input is already 250 ms
        switchMap(([searchTerm, user]) => memberService
          .searchAgents(
            searchTerm,
            this.onlyDelegatedUsers,
            this.excludeTeamMembers,
            this.searchBy,
            this.filterLicenseNumber,
            this.filterRealEstateRole,
            this.blacklistedIds,
          )
          .pipe(
            map((members) => members.filter((member) => member.userId !== user._id)),
            map((members) => members.filter((member) => (this.selectedIds || []).indexOf(member._id) < 0)),
            map((members) => members.filter((member) => (this.blacklistedIds || []).indexOf(member._id) < 0)),
            map((members) => members.filter(
              (member) => !(this.selected || []).find((s) => s.userId === member.userId),
            )),
          )),
      );
    }
  }
}
